import type {
  AgentProtectionQuery,
  AssociateResourcesRequest,
  DeployChangesRequest,
  GatewayCheckRequest,
  RemoveAssociationRequest,
  RetryFailedRequest,
  SaveNativePolicyRequest,
  ScenarioRequest,
  UpdateAssociationRequest,
  VerifyMappingRequest,
} from "@/api/temp/AgentProtectionService";
import { agentProtectionStore } from "./store";

/**
 * A short, visible pause on writes so loading and "Applying" states can be
 * seen in a walkthrough. None under Cypress, so specs never wait on it.
 */
const latency = (ms: number) =>
  typeof window !== "undefined" && "Cypress" in window ? 0 : ms;

const settle = <T>(run: () => T, ms: number): Promise<T> =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      try {
        resolve(run());
      } catch (error) {
        reject(error);
      }
    }, latency(ms));
  });

const WRITE_MS = 450;
const DEPLOY_MS = 1400;

/** `mockRegistry` entries for `AgentProtectionService`. */
export const agentProtectionMocks = {
  getAgentProtectionUsingGet: (params: AgentProtectionQuery) =>
    agentProtectionStore.getProtection(params),
  listProtectionCandidatesUsingGet: (params: AgentProtectionQuery) =>
    agentProtectionStore.listCandidates(params),
  getDataSourceCatalogUsingGet: (
    params: AgentProtectionQuery & { dataSourceId: string },
  ) => agentProtectionStore.getCatalog(params),
  associateAgentResourcesUsingPost: (params: AssociateResourcesRequest) =>
    settle(() => agentProtectionStore.associate(params), WRITE_MS),
  updateAgentResourceAssociationUsingPut: (params: UpdateAssociationRequest) =>
    settle(() => agentProtectionStore.updateAssociation(params), WRITE_MS),
  removeAgentResourceAssociationUsingDelete: (params: RemoveAssociationRequest) =>
    settle(() => agentProtectionStore.removeAssociation(params), WRITE_MS),
  saveNativePolicyDraftUsingPost: (params: SaveNativePolicyRequest) =>
    settle(() => agentProtectionStore.saveNativeDraft(params), WRITE_MS),
  verifyNativeRoleMappingUsingPost: (params: VerifyMappingRequest) =>
    settle(() => agentProtectionStore.verifyMapping(params), WRITE_MS),
  deployProtectionChangesUsingPost: (params: DeployChangesRequest) =>
    settle(() => agentProtectionStore.deploy(params), DEPLOY_MS),
  retryFailedProtectionOperationsUsingPost: (params: RetryFailedRequest) =>
    settle(() => agentProtectionStore.retryFailed(params), DEPLOY_MS),
  reassessAgentProtectionUsingPost: (params: AgentProtectionQuery) =>
    settle(() => agentProtectionStore.reassess(params), WRITE_MS),
  simulateGatewayCheckUsingPost: (params: GatewayCheckRequest) =>
    settle(() => agentProtectionStore.gatewayCheck(params), WRITE_MS),
  setProtectionScenarioUsingPut: (params: ScenarioRequest) =>
    agentProtectionStore.setScenario(params),
  resetAgentProtectionDemoUsingPost: (params: AgentProtectionQuery) =>
    agentProtectionStore.resetAgent(params),
};
